import pino from "pino";

const isEdge =
  typeof process !== "undefined" &&
  (process as any).env?.NEXT_RUNTIME === "edge";
const isBrowser = typeof window !== "undefined";

export type Logger = {
  info: (obj: object | string, msg?: string) => void;
  error: (obj: object | string, msg?: string) => void;
  debug: (obj: object | string, msg?: string) => void;
  warn: (obj: object | string, msg?: string) => void;
};

// Create a safe logger instance
let logger: Logger;

const SENSITIVE_KEYS = [
  "user.password",
  "headers.authorization",
  "idToken",
  "sessionCookie",
  "token",
  "sessionId",
];

if (isEdge || isBrowser) {
  // Use standard console for Edge Runtime and Browser to avoid Node.js dependency issues
  const createLogFn = (level: number, consoleFn: (...args: any[]) => void) => {
    return (obj: object | string, msg?: string) => {
      try {
        const logObj = typeof obj === "string" ? { msg: obj } : { msg, ...obj };

        // Manual redaction for non-pino environments
        const redactedLogObj = Object.fromEntries(
          Object.entries(logObj).map(([key, value]) =>
            SENSITIVE_KEYS.includes(key) ? [key, "[REDACTED]"] : [key, value]
          )
        );

        // In development browser, log the object directly for better DX
        // In production or edge, use JSON string for structured logging in GCP
        if (process.env.NODE_ENV === "development" && isBrowser) {
          consoleFn(`[${level}]`, redactedLogObj);
        } else {
          consoleFn(JSON.stringify({ level, ...redactedLogObj }));
        }
      } catch (e) {
        // Fallback if JSON.stringify fails (e.g. circular reference)
        // biome-ignore lint/suspicious/noConsole: this is the logger implementation
        console.error("[Logger Error] Failed to serialize log object", e);
      }
    };
  };

  logger = {
    // biome-ignore lint/suspicious/noConsole: this is the logger implementation
    info: createLogFn(30, console.log),
    // biome-ignore lint/suspicious/noConsole: this is the logger implementation
    error: createLogFn(50, console.error),
    // biome-ignore lint/suspicious/noConsole: this is the logger implementation
    debug: createLogFn(20, console.debug),
    // biome-ignore lint/suspicious/noConsole: this is the logger implementation
    warn: createLogFn(40, console.warn),
  };
} else {
  // Use full pino for Node.js environments (Cloud Run backend)
  logger = pino({
    level:
      process.env.LOG_LEVEL ||
      (process.env.NODE_ENV === "production" ? "info" : "debug"),
    redact: SENSITIVE_KEYS,
  });
}

export default logger;
